import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RotateCcw, Calculator } from "lucide-react";

const CIWA_ITEMS = [
  { id: "nausea", label: "Nausea / Vomiting" },
  { id: "tremor", label: "Tremor" },
  { id: "sweating", label: "Paroxysmal Sweats" },
  { id: "anxiety", label: "Anxiety" },
  { id: "agitation", label: "Agitation" },
  { id: "tactile", label: "Tactile Disturbances" },
  { id: "auditory", label: "Auditory Disturbances" },
  { id: "visual", label: "Visual Disturbances" },
  { id: "headache", label: "Headache / Fullness in Head" },
  { id: "orientation", label: "Orientation / Clouding" },
];

function getSeverity(score, variant) {
  if (variant === "wes") {
    if (score <= 1) return { label: "None", color: "bg-slate-500", textColor: "text-slate-400", bgColor: "bg-slate-500/10 border-slate-500/30", treatment: "No pharmacological treatment indicated" };
    if (score <= 9) return { label: "Mild", color: "bg-green-500", textColor: "text-green-500", bgColor: "bg-green-500/10 border-green-500/30", treatment: "Lorazepam 1 mg PO Q30min" };
    if (score <= 19) return { label: "Moderate", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30", treatment: "Diazepam 2.5-5 mg IV/IM Q30min until score <10" };
    return { label: "Severe", color: "bg-red-500", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30", treatment: "Diazepam 2.5-5 mg IV/IM Q30min until score <10. If no change after 10 mg Diazepam (~1hr): activate transport" };
  }
  // Ground default
  if (score <= 9) return { label: "Mild", color: "bg-green-500", textColor: "text-green-500", bgColor: "bg-green-500/10 border-green-500/30", treatment: "Lorazepam 1-4 mg SL" };
  if (score <= 19) return { label: "Moderate", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30", treatment: "Diazepam 10 mg IV q 10-15min" };
  return { label: "Severe", color: "bg-red-500", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30", treatment: "Diazepam 20 mg IV q 10-15min" };
}

export default function CIWACalculator({ variant = "ground" }) {
  const [scores, setScores] = useState({});
  const [open, setOpen] = useState(false);

  const total = Object.values(scores).reduce((sum, v) => sum + v, 0);
  const severity = getSeverity(total, variant);
  const answered = Object.keys(scores).length;

  const handleSelect = (itemId, value) => {
    setScores(prev => {
      if (prev[itemId] === value) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: value };
    });
  };

  const handleReset = () => setScores({});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="ciwa-trigger">
          <Calculator className="w-4 h-4" />
          CIWA-Ar Calculator
          {answered > 0 && (
            <Badge className={`${severity.color} text-white ml-1`}>{total}</Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">CIWA-Ar Score Calculator</DialogTitle>
          <p className="text-sm text-muted-foreground">Score each item 0-7. Tap to select, tap again to deselect.</p>
        </DialogHeader>

        {/* Score Summary */}
        <div className={`rounded-lg border p-4 ${severity.bgColor}`} data-testid="ciwa-score-summary">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-wide">Total Score</span>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-3xl font-bold font-mono ${severity.textColor}`}>{total}</span>
                <span className="text-sm text-muted-foreground">/ 70</span>
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-1">
              <Badge className={`${severity.color} text-white`}>{severity.label}</Badge>
              <Button variant="ghost" size="sm" onClick={handleReset} className="h-6 text-xs px-2" data-testid="ciwa-reset">
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            </div>
          </div>
          {answered > 0 && (
            <div className="mt-2 pt-2 border-t border-current/10">
              <span className="text-xs font-medium uppercase tracking-wide">Treatment</span>
              <p className={`font-semibold ${severity.textColor}`} data-testid="ciwa-treatment">{severity.treatment}</p>
            </div>
          )}
        </div>

        {/* Score Items */}
        <div className="space-y-3">
          {CIWA_ITEMS.map((item, idx) => (
            <div key={item.id} className="space-y-1.5" data-testid={`ciwa-item-${item.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{idx + 1}. {item.label}</span>
                {scores[item.id] !== undefined && (
                  <Badge variant="outline" className="font-mono text-xs">{scores[item.id]}</Badge>
                )}
              </div>
              <div className="flex gap-1">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(v => (
                  <button
                    key={v}
                    onClick={() => handleSelect(item.id, v)}
                    className={`flex-1 h-9 rounded-md text-sm font-mono font-bold transition-all ${
                      scores[item.id] === v
                        ? 'bg-primary text-primary-foreground ring-2 ring-primary/50'
                        : 'bg-secondary/40 hover:bg-secondary/80 text-foreground'
                    }`}
                    data-testid={`ciwa-${item.id}-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
